import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  TextField,
  Box,
  Button,
  IconButton,
  Typography,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { withTranslation } from 'react-i18next';
import axios from 'axios';
import DecimalField from '../shared/DecimalField';
import { useUserStore } from 'store';
import VendorPurchaseSection from './VendorPurchaseSection';
import { can } from '../../utils/access';

const API_INTEGRATION_NAMES = { orum: 'Örum', atoy: 'Atoy', meko: 'MEKO', ratparts: 'Rengastien Autotarvike' };

function VendorDialog({ t, open, handleClose, vendor, onCreated, onUpdated, onDeleted }) {
  const { currentOrganization } = useUserStore();
  const purchaseLedgerOn = currentOrganization?.purchase_ledger_available === true && can(currentOrganization, 'purchase_invoices.view');
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleFormChanged = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  useEffect(() => {
    setFormData(vendor || {});
    setErrors({});
    setShowDeleteConfirm(false);
    setSaving(false);
  }, [vendor, open]);

  const handleDelete = async () => {
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }
    try {
      await axios.delete(`/api/vendors/${vendor.id}/`);
      onDeleted?.();
      handleClose();
    } catch (error) {
      console.error('Failed to delete vendor:', error);
      if (error.response?.data) {
        setErrors(error.response.data);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      if (vendor) {
        const response = await axios.put(`/api/vendors/${vendor.id}/`, formData);
        onUpdated?.(response.data);
        handleClose();
      } else {
        const response = await axios.post('/api/vendors/', formData);
        onCreated?.(response.data);
        handleClose();
      }
    } catch (error) {
      console.error('Failed to save vendor:', error);
      if (error.response?.data) {
        setErrors(error.response.data);
      }
    } finally {
      setSaving(false);
    }
  };

  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
    },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      data-testid="vendor-dialog"
      PaperProps={{
        sx: {
          borderRadius: { xs: '12px', sm: '16px' },
          m: { xs: 2, sm: 4 },
          maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 700,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {vendor ? t('vendors.editVendor') : t('vendors.addVendor')}
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: 'text.secondary' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, px: { xs: 2, sm: 3 } }}>
        {/* Error messages */}
        {errors.non_field_errors && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.non_field_errors.join(', ')}
          </Alert>
        )}

        {/* Basic Information */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1.5, color: 'text.secondary' }}>
            {t('Basic Information')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              required
              id="name"
              name="name"
              label={t('vendors.name')}
              onChange={handleFormChanged}
              fullWidth
              value={formData.name || ''}
              error={!!errors.name}
              helperText={errors.name?.[0]}
              sx={textFieldStyles}
              autoFocus
            />
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField
                id="business_id"
                name="business_id"
                label={t('Business ID')}
                onChange={handleFormChanged}
                fullWidth
                value={formData.business_id || ''}
                error={!!errors.business_id}
                helperText={errors.business_id?.[0]}
                placeholder="1234567-8"
                sx={textFieldStyles}
              />
              <TextField
                id="contact_person"
                name="contact_person"
                label={t('Contact Person')}
                onChange={handleFormChanged}
                fullWidth
                value={formData.contact_person || ''}
                error={!!errors.contact_person}
                helperText={errors.contact_person?.[0]}
                sx={textFieldStyles}
              />
            </Box>
          </Box>
        </Box>

        {/* Contact Information */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1.5, color: 'text.secondary' }}>
            {t('vendors.contactInfo')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField
                id="email"
                name="email"
                label={t('Email Address')}
                type="email"
                onChange={handleFormChanged}
                fullWidth
                value={formData.email || ''}
                error={!!errors.email}
                helperText={errors.email?.[0]}
                sx={textFieldStyles}
              />
              <TextField
                id="phone_number"
                name="phone_number"
                label={t('Phone Number')}
                type="tel"
                onChange={handleFormChanged}
                fullWidth
                value={formData.phone_number || ''}
                error={!!errors.phone_number}
                helperText={errors.phone_number?.[0]}
                sx={textFieldStyles}
              />
            </Box>
          </Box>
        </Box>

        {/* Address */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1.5, color: 'text.secondary' }}>
            {t('vendors.address')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              id="address_street"
              name="address_street"
              label={t('Street Address')}
              onChange={handleFormChanged}
              fullWidth
              value={formData.address_street || ''}
              error={!!errors.address_street}
              helperText={errors.address_street?.[0]}
              sx={textFieldStyles}
            />
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField
                id="address_postal_code"
                name="address_postal_code"
                label={t('Postal Code')}
                onChange={handleFormChanged}
                value={formData.address_postal_code || ''}
                error={!!errors.address_postal_code}
                helperText={errors.address_postal_code?.[0]}
                sx={{ ...textFieldStyles, flex: 1 }}
              />
              <TextField
                id="address_city"
                name="address_city"
                label={t('City')}
                onChange={handleFormChanged}
                value={formData.address_city || ''}
                error={!!errors.address_city}
                helperText={errors.address_city?.[0]}
                sx={{ ...textFieldStyles, flex: 2 }}
              />
            </Box>
          </Box>
        </Box>

        {/* Ordering (sähköpostitilaus): an order email makes this vendor's parts orderable
            from the work order and the Varaosatilaukset page. */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1.5, color: 'text.secondary' }}>
            {t('vendors.ordering')}
          </Typography>
          <TextField
            id="order_email"
            name="order_email"
            label={t('vendors.orderEmail')}
            type="email"
            onChange={handleFormChanged}
            fullWidth
            disabled={!!formData.api_integration_type}
            value={formData.order_email || ''}
            error={!!errors.order_email}
            helperText={
              errors.order_email?.[0]
              || (formData.api_integration_type
                ? t('vendors.orderEmailApiIntegration', {
                  integration: API_INTEGRATION_NAMES[formData.api_integration_type] || formData.api_integration_type,
                })
                : t('vendors.orderEmailHelp'))
            }
            inputProps={{ 'data-testid': 'vendor-order-email' }}
            sx={textFieldStyles}
          />
        </Box>

        {/* Pricing */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1.5, color: 'text.secondary' }}>
            {t('vendors.pricing')}
          </Typography>
          <DecimalField
            label={t('vendors.partsMargin')}
            value={formData.parts_margin ?? ''}
            onChange={(value) => setFormData({ ...formData, parts_margin: value === '' ? null : value })}
            maxDecimals={2}
            fullWidth
            error={!!errors.parts_margin}
            helperText={errors.parts_margin?.[0] || t('vendors.partsMarginHelp')}
            InputProps={{ endAdornment: <Typography color="text.secondary">%</Typography> }}
            sx={textFieldStyles}
          />
        </Box>

        {/* Ostolaskutus (purchase ledger): AP terms, default cost category and bank accounts.
            Existing vendors only; saved through its own endpoints, not with the vendor form. */}
        {purchaseLedgerOn && vendor?.id && (
          <VendorPurchaseSection vendorId={vendor.id} textFieldStyles={textFieldStyles} />
        )}

        {/* Notes */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1.5, color: 'text.secondary' }}>
            {t('vendors.notes')}
          </Typography>
          <TextField
            id="notes"
            name="notes"
            label={t('Notes')}
            onChange={handleFormChanged}
            fullWidth
            multiline
            rows={3}
            value={formData.notes || ''}
            error={!!errors.notes}
            helperText={errors.notes?.[0]}
            sx={textFieldStyles}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'space-between' }}>
        {/* Delete button - only shown when editing */}
        {vendor ? (
          <Button
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={handleDelete}
            sx={{
              borderRadius: '10px',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {showDeleteConfirm ? <span>{t('Confirm delete')}</span> : <span>{t('Delete')}</span>}
          </Button>
        ) : (
          <Box />
        )}

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={handleClose}
            sx={{
              borderRadius: '10px',
              px: 3,
              color: 'text.secondary',
            }}
          >
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            data-testid="submit-vendor-button"
            onClick={handleSubmit}
            disabled={saving}
            sx={{
              borderRadius: '10px',
              px: 3,
              bgcolor: 'secondary.main',
              '&:hover': { bgcolor: 'secondary.dark' },
            }}
          >
            {vendor ? t('Save Changes') : t('vendors.addVendor')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default withTranslation()(VendorDialog);
